import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ContentBox from "../../../actprep-ui/components/ContentBox";
import layout from "../../../actprep-ui/constants/layout";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGet } from "../../../actprep-ui/hooks/api";
import { apiUrls, urls } from "../../../actprep-ui/utils/urls";
import { TESTING_APP_ORIGIN } from "../../../constants/misc";
import Paper from "@mui/material/Paper";
import dayjs from "dayjs";

const ACT_SUBJECTS = ["english", "math", "reading", "science"];

const sectionDescriptions = {
  effortRanking:
    "This ranking compares your student's effort against peers in their cohort. ACT scores do not affect this ranking.",
  weeklyStats:
    'These are the most critical weekly controllables. Red values indicate potential "red flags" that should be improved.',
};

const weeklyColumnHelp = {
  noon_check_ins:
    "Noon check-ins are an important consistency indicator. Missing morning check-ins can be an early warning sign that schedule structure is slipping.",
  check_ins:
    'To stay "In the Game," students should check in at least 5 days per week by noon, and ideally daily regardless of time.',
  total_sent:
    "Accuracy tracks whether students submitted their daily stats. This helps coaches verify progress and intervene quickly when needed.",
  actual_hours:
    "Shows actual hours completed versus needed hours to stay on track for a five-point increase.",
  actual_score:
    "Shows achieved weekly score versus needed score to stay on track for a five-point increase.",
};

const awardDescriptions = {
  foundation:
    "The Foundation Award is earned by checking in every day for 13 consecutive weeks.",
  gold_award:
    "The Gold Award is earned by completing at least 1.5 hours of prep, 6 days per week, with an 80%+ quiz average for 13 consecutive weeks.",
  persistence:
    "The Persistence Award is earned by checking in before noon at least 5 days per week for at least 11 of any 13 consecutive weeks.",
  determination:
    "The Determination Award is earned by finishing the program with an 80%+ quiz average.",
};

function scoreStatusColor(isGood) {
  return isGood ? "success" : "error";
}

function fullStudentName(student) {
  return `${student?.first_name || ""} ${student?.last_name || ""}`.trim();
}

function ordinalSuffix(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return "N/A";
  }
  const absNum = Math.abs(num);
  const modulo10 = absNum % 10;
  const modulo100 = absNum % 100;
  if (modulo10 === 1 && modulo100 !== 11) {
    return `${num}st`;
  }
  if (modulo10 === 2 && modulo100 !== 12) {
    return `${num}nd`;
  }
  if (modulo10 === 3 && modulo100 !== 13) {
    return `${num}rd`;
  }
  return `${num}th`;
}

function metricLabel(metric) {
  return metric.replaceAll("check_ins", "check-ins").replaceAll("_", " ");
}

function formatMetricStat(metric, value, percentile) {
  if (value === null || value === undefined) {
    return "N/A";
  }
  const roundedValue = Number.isFinite(Number(value))
    ? Number(value).toFixed(2).replace(".00", "")
    : value;
  const valueLabel = metric === "quiz_average" ? `${roundedValue}%` : roundedValue;
  if (percentile === null || percentile === undefined) {
    return valueLabel;
  }
  return `${valueLabel} (${ordinalSuffix(percentile)} percentile)`;
}

function scoreValue(student, scoreType, subject) {
  const nested = student?.scores?.[scoreType]?.[subject];
  if (nested !== null && nested !== undefined) {
    return nested;
  }
  return student?.[`${scoreType}_${subject}`] ?? null;
}

function cohortStartDate(student) {
  return student?.cohort_start_date || student?.cohort?.start_date || null;
}

function prettyLabel(value) {
  if (!value) {
    return "";
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatWeekRange(week) {
  if (!week?.start_date || !week?.end_date) {
    return "N/A";
  }
  return `${dayjs(week.start_date).format("MM/DD/YYYY")} - ${dayjs(
    week.end_date
  ).format("MM/DD/YYYY")}`;
}

function StatChip({
  value,
  target,
  goodWhenGte = true,
  suffix = "",
  shouldEvaluate = true,
}) {
  if (value === null || value === undefined) {
    return <Chip size="small" label="N/A" />;
  }
  const isGood = shouldEvaluate ? (goodWhenGte ? value >= target : value <= target) : true;
  const label =
    target !== undefined
      ? `${value}${suffix}/${target}${suffix}`
      : `${value}${suffix}`;
  return (
    <Chip
      size="small"
      color={shouldEvaluate ? scoreStatusColor(isGood) : "default"}
      label={label}
      sx={{ fontWeight: 600 }}
    />
  );
}

function LeaderboardTable({
  title,
  rows,
  selectedStudentRow,
  selectedStudentId,
  valueKey,
  valueLabel,
}) {
  const scrollRef = useRef(null);
  const selectedRowRef = useRef(null);

  const displayedRows = useMemo(() => {
    const topRows = rows || [];
    const selectedUuid = selectedStudentRow?.student?.uuid;
    // Only pin the selected student when the fetched row is actually theirs
    // and they are not already in the top leaderboard rows.
    const shouldPrepend =
      !!selectedStudentId &&
      selectedUuid === selectedStudentId &&
      !topRows.some((row) => row.student?.uuid === selectedStudentId);

    if (!shouldPrepend) {
      return topRows;
    }
    return [selectedStudentRow, ...topRows];
  }, [rows, selectedStudentId, selectedStudentRow]);

  useEffect(() => {
    if (!scrollRef.current) {
      return;
    }
    scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectedStudentId, title]);

  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [displayedRows, selectedStudentId]);

  return (
    <ContentBox sx={{ height: "100%" }}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <Typography variant="h6" color="primary" fontWeight="bold" mb={1}>
          {title}
        </Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          ref={scrollRef}
          sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Rank</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>School</TableCell>
                <TableCell>Team</TableCell>
                <TableCell align="right">{valueLabel}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRows.map((row) => {
                const isSelected = row.student?.uuid === selectedStudentId;
                return (
                  <TableRow
                    key={`${title}-${row.id || row.rank}-${row.student?.uuid}`}
                    ref={isSelected ? selectedRowRef : null}
                    sx={{ bgcolor: isSelected ? "success.light" : "inherit" }}
                  >
                    <TableCell>{row.rank}</TableCell>
                    <TableCell>
                      {row.student?.first_name} {row.student?.last_name}
                    </TableCell>
                    <TableCell>{row.student?.school?.name || "N/A"}</TableCell>
                    <TableCell>{row.current_team || "N/A"}</TableCell>
                    <TableCell align="right">{row[valueKey]}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </ContentBox>
  );
}

export default function Dashboard({
  targetParentProfile,
  showViewingParentLabel = false,
}) {
  const parentId = targetParentProfile?.uuid;
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [infoDialogKey, setInfoDialogKey] = useState("");
  const [selectedAwardKey, setSelectedAwardKey] = useState("");

  const { data: studentsData, isLoading: isLoadingStudents } = useGet(
    apiUrls.parent(parentId).students(),
    {
      enabled: !!parentId,
      params: {},
    }
  );

  const students = useMemo(() => studentsData?.data?.students ?? [], [studentsData]);

  const activeStudentId = useMemo(() => {
    if (!students.length) {
      return "";
    }
    if (students.some((student) => student.uuid === selectedStudentId)) {
      return selectedStudentId;
    }
    return students[0].uuid;
  }, [selectedStudentId, students]);

  const selectedStudent =
    students.find((student) => student.uuid === activeStudentId) || null;

  const parentApi = parentId ? apiUrls.parent(parentId) : null;

  const { data: rankingData, isLoading: isLoadingRanking } = useGet(
    parentApi?.studentRanking(activeStudentId) || "",
    {
      enabled: !!parentApi && !!activeStudentId,
      params: {},
    }
  );
  const { data: weeklyData, isLoading: isLoadingWeekly } = useGet(
    parentApi?.studentWeeklyData(activeStudentId) || "",
    {
      enabled: !!parentApi && !!activeStudentId,
      params: {},
    }
  );
  const { data: awardsData, isLoading: isLoadingAwards } = useGet(
    parentApi?.studentAwards(activeStudentId) || "",
    {
      enabled: !!parentApi && !!activeStudentId,
      params: {},
    }
  );
  const { data: scoreGoodNewsData, isLoading: isLoadingScoreGoodNews } = useGet(
    parentApi?.goodNews("score") || "",
    {
      enabled: !!parentApi && !!activeStudentId,
      params: { page: 1, size: 5 },
    }
  );
  const {
    data: selectedStudentScoreGoodNewsData,
    isLoading: isLoadingSelectedStudentScoreGoodNews,
  } = useGet(parentApi?.goodNews("score") || "", {
    enabled: !!parentApi && !!activeStudentId,
    params: {
      page: 1,
      size: 1,
      search_params: { student__uuid: activeStudentId },
    },
  });
  const { data: quizGoodNewsData, isLoading: isLoadingQuizGoodNews } = useGet(
    parentApi?.goodNews("quiz_score") || "",
    {
      enabled: !!parentApi && !!activeStudentId,
      params: { page: 1, size: 5 },
    }
  );
  const {
    data: selectedStudentQuizGoodNewsData,
    isLoading: isLoadingSelectedStudentQuizGoodNews,
  } = useGet(parentApi?.goodNews("quiz_score") || "", {
    enabled: !!parentApi && !!activeStudentId,
    params: {
      page: 1,
      size: 1,
      search_params: { student__uuid: activeStudentId },
    },
  });
  const { data: increaseGoodNewsData, isLoading: isLoadingIncreaseGoodNews } =
    useGet(parentApi?.goodNews("total_increase") || "", {
      enabled: !!parentApi && !!activeStudentId,
      params: { page: 1, size: 5 },
    });
  const {
    data: selectedStudentIncreaseGoodNewsData,
    isLoading: isLoadingSelectedStudentIncreaseGoodNews,
  } = useGet(parentApi?.goodNews("total_increase") || "", {
    enabled: !!parentApi && !!activeStudentId,
    params: {
      page: 1,
      size: 1,
      search_params: { student__uuid: activeStudentId },
    },
  });

  const isLoadingMonitoring =
    isLoadingStudents ||
    isLoadingRanking ||
    isLoadingWeekly ||
    isLoadingAwards ||
    isLoadingScoreGoodNews ||
    isLoadingQuizGoodNews ||
    isLoadingIncreaseGoodNews ||
    isLoadingSelectedStudentScoreGoodNews ||
    isLoadingSelectedStudentQuizGoodNews ||
    isLoadingSelectedStudentIncreaseGoodNews;

  const ranking = rankingData?.data || {};
  const weeklyReports = weeklyData?.data?.weeks || [];
  const awardStatus = awardsData?.data || {};
  const activeAwardWeeks = awardStatus?.award_weeks || [];

  const activeAwardInfo = selectedAwardKey
    ? {
        key: selectedAwardKey,
        name: metricLabel(selectedAwardKey),
        description: awardDescriptions[selectedAwardKey],
        failedWeeks: activeAwardWeeks.filter((week) => !week[selectedAwardKey]),
      }
    : null;

  const scoreGoodNewsRows = scoreGoodNewsData?.data?.weeks || [];
  const quizGoodNewsRows = quizGoodNewsData?.data?.weeks || [];
  const increaseGoodNewsRows = increaseGoodNewsData?.data?.weeks || [];
  const selectedStudentScoreGoodNewsRow =
    selectedStudentScoreGoodNewsData?.data?.weeks?.[0] || null;
  const selectedStudentQuizGoodNewsRow =
    selectedStudentQuizGoodNewsData?.data?.weeks?.[0] || null;
  const selectedStudentIncreaseGoodNewsRow =
    selectedStudentIncreaseGoodNewsData?.data?.weeks?.[0] || null;
  const scoreSubject = scoreGoodNewsRows?.[0]?.subject;

  const studentHasNotStarted =
    selectedStudent &&
    (!selectedStudent.enrollment_completed ||
      (cohortStartDate(selectedStudent) &&
        dayjs(cohortStartDate(selectedStudent)).isAfter(dayjs(), "day") &&
        (selectedStudent.check_ins || 0) === 0));

  const scoreColumns = [
    { key: "start", label: "Starting" },
    { key: "best", label: "Best" },
    { key: "projected", label: "Projected" },
  ];

  return (
    <Box
      sx={{
        my: `${layout.navBarHeight + 1}rem`,
        width: "100%",
      }}
    >
      <Typography variant="h5" color="primary" fontWeight="bold" mb={2}>
        Dashboard
      </Typography>
      {showViewingParentLabel && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          Viewing parent: {targetParentProfile?.label || "Unknown Parent"}
        </Typography>
      )}

      <ContentBox sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="student-select-label">Student</InputLabel>
              <Select
                labelId="student-select-label"
                value={activeStudentId}
                label="Student"
                onChange={(event) => setSelectedStudentId(event.target.value)}
              >
                {students.map((student) => (
                  <MenuItem key={student.uuid} value={student.uuid}>
                    {fullStudentName(student)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Typography variant="body2" color="text.secondary">
              Track rankings, weekly controllables, awards, score trends, and
              leaderboards in one view.
            </Typography>
          </Grid>
          <Grid
            size={{ xs: 12, md: 3 }}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Button
              variant="outlined"
              size="small"
              disabled={!activeStudentId || !TESTING_APP_ORIGIN}
              onClick={() =>
                window.open(
                  `${TESTING_APP_ORIGIN}${urls.students.impersonate(activeStudentId)}`,
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            >
              View student dashboard
            </Button>
          </Grid>
        </Grid>
        {isLoadingStudents && <LinearProgress sx={{ mt: 2 }} />}
      </ContentBox>

      {!students.length && !isLoadingStudents && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No students are currently linked to this parent profile.
        </Alert>
      )}

      {studentHasNotStarted && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This student has not started the program yet. Program start date:{" "}
          {dayjs(cohortStartDate(selectedStudent)).format("MMMM D, YYYY")}
        </Alert>
      )}

      {!studentHasNotStarted && !!selectedStudent && (
        <Grid container spacing={2} alignItems="stretch">
          <Grid size={{ xs: 12, lg: 7 }} sx={{ height: { lg: "72vh" }, minHeight: 0 }}>
            <ContentBox sx={{ height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Weekly Performance
                  </Typography>
                  <Tooltip title="More info">
                    <IconButton
                      size="small"
                      onClick={() => setInfoDialogKey("weeklyStats")}
                      aria-label="Weekly stats info"
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {sectionDescriptions.weeklyStats}
                </Typography>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Week</TableCell>
                        <TableCell>Program Week</TableCell>
                        <TableCell>Noon Check-ins</TableCell>
                        <TableCell>Check-ins</TableCell>
                        <TableCell>Accuracy</TableCell>
                        <TableCell>Quiz Score</TableCell>
                        <TableCell>Hours</TableCell>
                        <TableCell>Score</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {weeklyReports.map((week) => {
                        const neededHours = Math.max(
                          Number(week.five_point_hours_needed || 0),
                          9
                        );
                        const neededScore = Number(week.five_point_score_needed || 0);
                        const isCurrentWeek =
                          !dayjs().isBefore(dayjs(week.start_date), "day") &&
                          !dayjs().isAfter(dayjs(week.end_date), "day");
                        return (
                          <TableRow key={week.uuid}>
                            <TableCell>{formatWeekRange(week)}</TableCell>
                            <TableCell>{week.verbose_name || "N/A"}</TableCell>
                            <TableCell>
                              <Tooltip title={weeklyColumnHelp.noon_check_ins} placement="top">
                                <Box>
                                  <StatChip
                                    value={week.noon_check_ins}
                                    target={5}
                                    shouldEvaluate={!isCurrentWeek}
                                  />
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={weeklyColumnHelp.check_ins} placement="top">
                                <Box>
                                  <StatChip
                                    value={week.check_ins}
                                    target={7}
                                    shouldEvaluate={!isCurrentWeek}
                                  />
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={weeklyColumnHelp.total_sent} placement="top">
                                <Box>
                                  <StatChip
                                    value={week.total_sent}
                                    target={5}
                                    shouldEvaluate={!isCurrentWeek}
                                  />
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <StatChip
                                value={week.quiz_score}
                                target={80}
                                suffix="%"
                                shouldEvaluate={!isCurrentWeek}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip title={weeklyColumnHelp.actual_hours} placement="top">
                                <Box>
                                  <StatChip
                                    value={week.actual_hours.toFixed(2)}
                                    target={neededHours}
                                    shouldEvaluate={!isCurrentWeek}
                                  />
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={weeklyColumnHelp.actual_score} placement="top">
                                <Box>
                                  <StatChip
                                    value={week.actual_score}
                                    target={neededScore}
                                    shouldEvaluate={!isCurrentWeek}
                                  />
                                </Box>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </ContentBox>
          </Grid>

          <Grid
            size={{ xs: 12, lg: 5 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minHeight: 0,
              height: { lg: "72vh" },
            }}
          >
            <ContentBox>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  Effort Ranking
                </Typography>
                <Tooltip title="More info">
                  <IconButton
                    size="small"
                    onClick={() => setInfoDialogKey("effortRanking")}
                    aria-label="Effort ranking info"
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {sectionDescriptions.effortRanking}
              </Typography>
              <Typography variant="h3" color="primary.main" fontWeight={700}>
                {ordinalSuffix(ranking?.overall_percentile)}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Percentile
              </Typography>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1, mb: 1 }}>
                Totals
              </Typography>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(ranking?.metrics || []).map((metric) => (
                      <TableRow key={metric}>
                        <TableCell>{metricLabel(metric)}</TableCell>
                        <TableCell>
                          {formatMetricStat(
                            metric,
                            ranking?.[metric],
                            ranking?.[`${metric}_percentile`]
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </ContentBox>

            <ContentBox sx={{ flex: 1, minHeight: 0 }}>
              <Typography variant="h6" color="primary" fontWeight="bold" mb={1}>
                Award Status
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                How is your student progressing toward each award?
              </Typography>
              <Stack spacing={1}>
                {Object.entries(awardStatus?.awards || {}).map(([awardKey, onTrack]) => (
                  <Box
                    key={awardKey}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {awardKey.replaceAll("_", " ")}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        color={onTrack ? "success" : "error"}
                        label={onTrack ? "On Track" : "Off Track"}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setSelectedAwardKey(awardKey)}
                      >
                        More Info
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </ContentBox>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <ContentBox>
              <Typography variant="h6" color="primary" fontWeight="bold" mb={1}>
                Score Snapshot
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Compare where your student started, where they have peaked, and
                where they are currently projected.
              </Typography>
              <Grid container spacing={2}>
                {scoreColumns.map((column) => (
                  <Grid key={column.key} size={{ xs: 12, md: 4 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor:
                          column.key === "best"
                            ? "success.50"
                            : column.key === "projected"
                              ? "info.50"
                              : "background.paper",
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {column.label}
                        </Typography>
                        <Chip
                          size="small"
                          color={column.key === "best" ? "success" : "primary"}
                          variant="outlined"
                          label={`Composite ${
                            scoreValue(selectedStudent, column.key, "composite") ?? "N/A"
                          }`}
                        />
                      </Stack>
                      <Stack spacing={0.75}>
                        {ACT_SUBJECTS.map((subject) => (
                          <Box
                            key={`${column.key}-${subject}`}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              px: 1,
                              py: 0.75,
                              borderRadius: 1,
                              bgcolor: "background.default",
                            }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {prettyLabel(subject)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {scoreValue(selectedStudent, column.key, subject) ?? "N/A"}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Note: &quot;Best&quot; reflects each student&apos;s highest practice test
                results.
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Note: Students cannot view projected scores.
              </Typography>
            </ContentBox>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }} sx={{ height: { md: "42vh" }, minHeight: 0 }}>
            <LeaderboardTable
              title={`Test Score Leaderboard${
                scoreSubject ? ` (${scoreSubject})` : ""
              }`}
              rows={scoreGoodNewsRows}
              selectedStudentRow={selectedStudentScoreGoodNewsRow}
              selectedStudentId={activeStudentId}
              valueKey="score"
              valueLabel="Score"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ height: { md: "42vh" }, minHeight: 0 }}>
            <LeaderboardTable
              title="Quiz Leaderboard"
              rows={quizGoodNewsRows}
              selectedStudentRow={selectedStudentQuizGoodNewsRow}
              selectedStudentId={activeStudentId}
              valueKey="quiz_score"
              valueLabel="Quiz Score"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ height: { md: "42vh" }, minHeight: 0 }}>
            <LeaderboardTable
              title={`Increase Leaderboard${
                scoreSubject ? ` (${scoreSubject})` : ""
              }`}
              rows={increaseGoodNewsRows}
              selectedStudentRow={selectedStudentIncreaseGoodNewsRow}
              selectedStudentId={activeStudentId}
              valueKey="total_increase"
              valueLabel="Increase"
            />
          </Grid>
        </Grid>
      )}

      {isLoadingMonitoring && <LinearProgress sx={{ mt: 2 }} />}

      <Dialog
        open={!!infoDialogKey}
        onClose={() => setInfoDialogKey("")}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {infoDialogKey === "effortRanking" ? "Effort Ranking" : "Weekly Stats"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {sectionDescriptions[infoDialogKey] || "No details available."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogKey("")}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!selectedAwardKey}
        onClose={() => setSelectedAwardKey("")}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{activeAwardInfo?.name || "Award Details"}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" mb={2}>
            {activeAwardInfo?.description || "No award description available."}
          </Typography>
          {selectedAwardKey === "determination" ? (
            <Typography variant="body2">
              Current quiz average: {selectedStudent?.quiz_average ?? "N/A"}%
            </Typography>
          ) : (
            <Box>
              <Typography variant="body2" fontWeight={700} mb={1}>
                Missed Weeks
              </Typography>
              {activeAwardInfo?.failedWeeks?.length ? (
                <Stack component="ul" sx={{ pl: 2, m: 0 }}>
                  {activeAwardInfo.failedWeeks.map((week) => (
                    <Typography component="li" variant="body2" key={week.uuid}>
                      {week.verbose_name}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2">No missed weeks listed.</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedAwardKey("")}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
